import {
  Check,
  CheckCheck,
  Clock,
  Loader2,
  FileText,
  Image,
} from "lucide-react"
import PropTypes from "prop-types"
import MarkdownRenderer from "@/components/MarkdownRenderer"
import { FileViewer } from "@/components/FileViewer"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

function MessageBubble({ message,patientId,messageId,sending }) {
  const isAI = message.type === "AI"
  const isStaff = message.type === "STAFF" || message.type === "STAFF_FILE"
  const isUser = message.type === "HUMAN" || message.type === "HUMAN_FILE"

  // For sending messages, treat them as user messages
  const isFromMe = message.is_from_me || sending

  const getMessageStyle = () => {
    if (message.type === "AI") {
      // AI message
      return {
        colors:
          "bg-purple-100 dark:bg-purple-900 text-purple-900 dark:text-purple-100",
        timeColor: "text-purple-600 dark:text-purple-300",
        senderColor: "text-purple-500 dark:text-purple-300",
      }
    } else if (message.type === "STAFF") {
      // STAFF/Staff message
      return {
        colors:
          "bg-emerald-100 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-100",
        timeColor: "text-emerald-600 dark:text-emerald-300",
        senderColor: "text-emerald-500 dark:text-emerald-300",
      }
    } else {
      // Human/User message
      return {
        colors: "bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100",
        timeColor: "text-blue-600 dark:text-blue-300",
        senderColor: "text-blue-500 dark:text-blue-300",
      }
    }
  }

  const style = getMessageStyle()

  const getStatusIcon = () => {
    if (sending) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className="flex items-center gap-0.5 text-gray-400">
                <Loader2 className="w-3 h-3 animate-spin" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Sending message...</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    let metaData = null
    try {
      metaData =
        typeof message.meta === "string"
          ? JSON.parse(message.meta)
          : message.meta
    } catch (e) {
      console.warn("Failed to parse message meta:", e)
    }

    const messageStatus = metaData?.status || message.status?.status
    const timestamp = metaData?.timestamp
      ? new Date(parseInt(metaData.timestamp) * 1000).toLocaleString()
      : null

    const getTooltipContent = () => {
      if (!metaData) return "No information available"
      return `Status: ${messageStatus || "Unknown"}\n ${messageStatus} at : ${
        timestamp || "Unknown"
      }`
    }

    const statusIcon = (() => {
      switch (messageStatus?.toUpperCase()) {
        case "READ":
          return <CheckCheck className="w-3 h-3 text-blue-500" />
        case "DELIVERED":
          return <CheckCheck className="w-3 h-3 text-gray-400" />
        case "SENT":
          return <Check className="w-3 h-3 text-gray-400" />
        default:
          return <Check className="w-3 h-3 text-gray-400" />
      }
    })()

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>{statusIcon}</TooltipTrigger>
          <TooltipContent>
            <p className="whitespace-pre-line">{getTooltipContent()}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return ""

    const date = new Date(timestamp)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()

    if (isToday) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    }
  }

  const getMessageTimestamp = () => {
    let metaData = null
    try {
      metaData =
        typeof message.meta === "string"
          ? JSON.parse(message.meta)
          : message.meta
    } catch (e) {
      console.warn("Failed to parse message meta:", e)
    }

    // Use sent_at from meta if available, otherwise fall back to modified_at
    const timestamp = metaData?.sent_at
      ? new Date(parseInt(metaData.sent_at)).toISOString()
      : message.modified_at

    return formatMessageTime(timestamp)
  }

  const messageTime = formatMessageTime(
    message.meta?.sent_at || message.created_at
  )

  const renderContent = () => {
    // Handle file type messages
    if (
      message.type === "FILE_TYPE" ||
      message.type === "STAFF_FILE" ||
      message.type === "HUMAN_FILE"
    ) {
      let metaData = null
      try {
        metaData =
          typeof message.meta === "string"
            ? JSON.parse(message.meta)
            : message.meta
      } catch (e) {
        console.warn("Failed to parse message meta:", e)
        return <span className="text-red-500">Invalid file data</span>
      }

      const isImage = metaData?.file_type?.startsWith("image/")
      const isPdf = metaData?.file_type?.includes("pdf")
      const filePath = metaData?.file_path || message.content

      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 bg-white/50 dark:bg-gray-800/50 p-2 rounded">
            {isImage ? (
              <Image className="w-4 h-4" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            <span className="text-sm flex-1 truncate">
              {metaData?.file_name}
            </span>
          </div>
          {!sending && (
            <FileViewer
              filePath={filePath}
              fileType={metaData?.file_type}
              fileName={metaData?.file_name}
              messageId={messageId}
              patientId={patientId}
              previewClassName="max-w-[300px]"
            />
          )}
        </div>
      )
    }

    // Handle regular text messages with markdown
    return <MarkdownRenderer content={message.content} />
  }

  return (
    <div
      className={`flex ${isAI || isStaff ? "justify-end" : "justify-start"} mb-4 relative`}
    >
      <div
        className={`max-w-[70%] p-3 rounded-lg ${style.colors} shadow-xs ${sending ? "opacity-85" : ""}`}
      >
        {renderContent()}

        {/* Only show this section for non-file messages */}

        <div className="flex items-center justify-between gap-2 mt-1">
          <div className={`text-[10px] ${style.timeColor}`}>
            {isAI && "Generated by AI"}
            {isUser && message.SenderName && `Sent by ${message.SenderName}`}
            {isStaff && message.SenderName}
          </div>

          <div className="flex items-center gap-2">
            <span className={`text-xs ${style.timeColor}`}>
              {sending ? "Sending..." : getMessageTimestamp()}
            </span>
            {(isAI || isStaff || message.is_from_me) && !sending && (
              <span className={`flex items-center ${style.timeColor}`}>
                {getStatusIcon()}
              </span>
            )}
            {sending && (
              <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

MessageBubble.propTypes = {
  message: PropTypes.shape({
    type: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    modified_at: PropTypes.string.isRequired,
    meta: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        status: PropTypes.string,
        file_name: PropTypes.string,
        file_type: PropTypes.string,
        file_path: PropTypes.string,
        read_at: PropTypes.number,
        sent_at: PropTypes.number,
        delivered_at: PropTypes.number,
        failureResponse: PropTypes.object,
      }),
    ]),
    SenderName: PropTypes.string,
    is_from_me: PropTypes.bool,
    status: PropTypes.shape({
      status: PropTypes.string,
    }),
  }).isRequired,
  sending: PropTypes.bool,
}

export default MessageBubble
